import { useDispatch, useSelector } from "react-redux";
import useRouter from "hooks/useRouter";
import getSearchResults from "api/requests/getSearchResults";
import { getCountryCode, getUserLocation } from "redux/selectors/location";
import {
  SEARCH_LOADING,
  SEARCH_RESULTS,
  SEARCH_LOAD_MORE,
  MAX_DISTANCE,
  SearchLoadingAction,
  SearchResultsAction,
  SearchLoadMoreAction,
} from "redux/reducers/search";
import {
  getSearchLoading,
  getSearchData,
  getSearchMenuItemCategories,
  getSearchMenuItemDispensaries,
  getSearchCategoryRelevanceOrder,
} from "redux/selectors/search";
import { SearchPageTypes } from "custom-types/Search";

const useSearch = () => {
  const {
    parsedQuery: { q, searchCategory },
  } = useRouter();

  const dispatch = useDispatch();
  const countryCode = useSelector(getCountryCode);
  const coordinates = useSelector(getUserLocation);
  const loading = useSelector(getSearchLoading);
  const results = useSelector(getSearchData);
  const menuItemCategories = useSelector(getSearchMenuItemCategories);
  const menuItemDispensaries = useSelector(getSearchMenuItemDispensaries);
  const categoryRelevanceOrder = useSelector(getSearchCategoryRelevanceOrder);

  const getResults = async (take = 12) => {
    dispatch<SearchLoadingAction>({
      type: SEARCH_LOADING,
    });

    const {
      article,
      brand,
      dispensary,
      menuItem,
      strain,
      menuItemCategories,
      menuItemDispensaries,
      categoryRelevanceOrder,
    } = await getSearchResults(
      { q, "filter[type]": searchCategory },
      coordinates,
      MAX_DISTANCE,
      countryCode,
      take
    );

    dispatch<SearchResultsAction>({
      type: SEARCH_RESULTS,
      results: {
        article,
        brand,
        dispensary,
        menuItem,
        strain,
        menuItemCategories,
        menuItemDispensaries,
        categoryRelevanceOrder,
      },
    });
  };

  const loadMore = async (type: SearchPageTypes, viewingCount: number) => {
    const numberToLoad = viewingCount - results[type].length;

    if (numberToLoad > 0) {
      const additionalItems = await getSearchResults(
        { q, "filter[type]": type },
        coordinates,
        MAX_DISTANCE,
        countryCode,
        numberToLoad,
        results[type].length
      );

      const newResults = [...results[type], ...additionalItems[type]];

      dispatch<SearchLoadMoreAction>({
        type: SEARCH_LOAD_MORE,
        result: { [type]: newResults },
      });
    }
  };

  return {
    loading,
    ...results,
    menuItemCategories,
    menuItemDispensaries,
    categoryRelevanceOrder,
    searchTerm: q,
    maxDistance: MAX_DISTANCE,
    getSearchResults: getResults,
    loadMoreSearchResults: loadMore,
  };
};

export default useSearch;
